import { useTranslationContext } from "../context/translation.js";
import { ComponentChildren, h } from "preact";

export function Footer({
  testingUrlKey,
  VERSION,
  GIT_HASH,
  backgroundClass,
  actions,
  variant = "default",
}: {
  VERSION?: string;
  GIT_HASH?: string;
  testingUrlKey?: string;
  backgroundClass?: string;
  actions?: ComponentChildren;
  variant?: "default" | "demo" | "compact";
}) {
  const { i18n } = useTranslationContext();
  const copyrightLabel = i18n.str`Copyright`;

  const testingUrl =
    testingUrlKey &&
    typeof localStorage !== "undefined" &&
    localStorage.getItem(testingUrlKey)
      ? (localStorage.getItem(testingUrlKey) ?? undefined)
      : undefined;
  const versionText = VERSION ? (
    GIT_HASH ? (
      <a
        href={`https://git.taler.net/wallet-core.git/tree/?id=${GIT_HASH}`}
        target="_blank"
        rel="noreferrer noopener"
      >
        {i18n.str`Version ${VERSION} (${GIT_HASH.substring(0, 8)})`}
      </a>
    ) : (
      VERSION
    )
  ) : (
    ""
  );
  const effectiveBackgroundClass =
    backgroundClass ??
    (variant === "default" ? "bg-slate-100 dark:bg-slate-900" : "");
  const testingNotice = testingUrlKey && testingUrl && (
    <p
      class={`text-xs leading-5 ${
        variant === "demo"
          ? "text-secondary"
          : "text-slate-700 dark:text-slate-200"
      }`}
    >
      <i18n.Translate>
        Testing with <span>{testingUrl}</span>
      </i18n.Translate>{" "}
      <button
        type="button"
        class="font-semibold underline hover:text-primary"
        onClick={() => {
          localStorage.removeItem(testingUrlKey);
          window.location.reload();
        }}
      >
        <i18n.Translate>stop testing</i18n.Translate>
      </button>
    </p>
  );

  if (variant === "compact") {
    return (
      <footer
        class={`mt-auto border-t border-slate-200 dark:border-slate-700 ${effectiveBackgroundClass}`}
      >
        <div class="mx-auto flex max-w-screen-2xl flex-wrap items-center gap-x-4 gap-y-1 px-6 py-2 text-xs leading-5 text-slate-600 sm:px-8 dark:text-slate-300">
          <p class="m-0">
            <i18n.Translate>
              Learn more about{" "}
              <a
                target="_blank"
                rel="noreferrer noopener"
                class="font-semibold text-slate-700 underline hover:text-primary dark:text-slate-100"
                href="https://taler.net"
              >
                GNU Taler
              </a>
            </i18n.Translate>
            <span aria-hidden="true"> · </span>
            {copyrightLabel} &copy; 2014&mdash;2026 Taler Systems SA.{" "}
            {versionText}
          </p>
          {actions ? <div class="shrink-0">{actions}</div> : undefined}
          {testingNotice ? (
            <div class="basis-full">{testingNotice}</div>
          ) : undefined}
        </div>
      </footer>
    );
  }

  if (variant === "demo") {
    return (
      <footer
        class={`mt-auto border-t border-outlineVariant ${effectiveBackgroundClass}`}
      >
        <div class="mx-auto flex min-h-[4.25rem] w-[calc(100%-2rem)] max-w-[68rem] flex-wrap items-center justify-between gap-x-5 gap-y-2 py-4 text-[0.82rem] text-secondary">
          <p class="m-0">
            <a
              target="_blank"
              rel="noreferrer noopener"
              class="text-inherit underline hover:text-primary"
              href="https://taler.net"
            >
              <i18n.Translate>
                Learn more about <span>GNU Taler</span>
              </i18n.Translate>
            </a>
            .
          </p>
          <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
            <p class="m-0">
              {copyrightLabel} &copy; 2014&mdash;2026 Taler Systems SA.{" "}
              {versionText}
            </p>
            {actions ? <div class="shrink-0">{actions}</div> : undefined}
          </div>
          {testingNotice ? (
            <div class="basis-full">{testingNotice}</div>
          ) : undefined}
        </div>
      </footer>
    );
  }

  return (
    <footer
      class={`mt-auto border-t border-slate-300 dark:border-slate-700 ${effectiveBackgroundClass}`}
    >
      <div class="mx-auto flex max-w-screen-2xl items-start justify-between gap-4 px-6 py-4 sm:px-8">
        <div>
          <p class="text-xs leading-5 text-slate-700 dark:text-slate-200">
            <i18n.Translate>
              Learn more about{" "}
              <a
                target="_blank"
                rel="noreferrer noopener"
                class="font-semibold text-slate-900 underline hover:text-primary dark:text-white"
                href="https://taler.net"
              >
                GNU Taler
              </a>
            </i18n.Translate>
          </p>
          <p class="text-xs leading-5 text-slate-700 dark:text-slate-200">
            {copyrightLabel} &copy; 2014&mdash;2026 Taler Systems SA.{" "}
            {versionText}{" "}
          </p>
        </div>
        {actions ? <div class="shrink-0">{actions}</div> : undefined}
      </div>
      {testingNotice ? (
        <div class="mx-auto max-w-screen-2xl px-6 pb-4 sm:px-8">
          {testingNotice}
        </div>
      ) : undefined}
    </footer>
  );
}
